/* eslint-disable react-refresh/only-export-components -- cva variants co-located with component */
import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/cn'

// ── Text Variants (CVA) ─────────────────────────────────────

export const textVariants = cva('', {
  variants: {
    variant: {
      'display-lg':  'text-display-lg',
      'display-sm':  'text-display-sm',
      'headline-md': 'text-headline-md',
      'headline-sm': 'text-headline-sm',
      'title-md':    'text-title-md',
      'body-md':     'text-body-md',
      'body-sm':     'text-body-sm',
      'label-lg':    'text-label-lg',
      'label-sm':    'text-label-sm',
    },
    color: {
      default: 'text-text',
      muted:   'text-text-muted',
      subtle:  'text-text-subtle',
      primary: 'text-primary',
      success: 'text-success',
      warning: 'text-warning',
      danger:  'text-danger',
      info:    'text-info',
      // Light text on a dark/fixed-dark surface (e.g. the /signup hero panel's --ink background).
      // Maps to the existing --color-text-inverse token — no new token introduced. Added as a variant
      // rather than overridden via className: a bare className override of a Text color utility risks
      // losing to an unlayered same-specificity class depending on which variant it pairs with (see
      // docs/architecture/design/DD-profile-action-dialogs.md's cascade-layers precedent) — going
      // through the component's own variant API sidesteps that class of bug entirely.
      inverse: 'text-text-inverse',
      // Two muted tiers of `inverse`, mirroring the existing default/muted/subtle hierarchy —
      // consolidated from five ad-hoc `opacity-NN` classNames duplicated across the /signup hero
      // (tagline, body, detail, footer, and two ControlPlaneArtwork stat labels), each independently
      // tuned for WCAG AA against --ink during that feature's implementation. Collapsing three
      // slightly different values (40/50/70 as first drafted, tuned to 55/60/70) into these two
      // named tiers only ever RAISES opacity (55%→60%), never lowers it, so contrast strictly
      // improves for every consolidated call site — verified: inverse-muted (70%) measures 7.69:1
      // against --ink, inverse-subtle (60%) measures 5.94:1, both clear of the 4.5:1 floor with
      // real margin. A future call site changing one number here changes it everywhere consistently,
      // rather than needing to know four other unlabeled duplicates exist.
      'inverse-muted': 'text-text-inverse opacity-70',
      'inverse-subtle': 'text-text-inverse opacity-60',
    },
    weight: {
      normal:    'font-normal',
      medium:    'font-medium',
      semibold:  'font-semibold',
      bold:      'font-bold',
      extrabold: 'font-extrabold',
    },
    mono: {
      // `font-mono` ALONE DOES NOT WIN. It is a Tailwind utility, so it lives in `@layer utilities`,
      // while the `text-*` scale in index.css is unlayered — and unlayered CSS beats every cascade
      // layer regardless of specificity or source order. `text-body-sm font-mono` therefore rendered
      // in the sans face on every variant that declares a family, which is all of them.
      // `font-mono-face` is the same declaration, unlayered and placed after the scale, so it is the
      // one that actually reaches the screen. `font-mono` stays because it is what a reader and
      // devtools look for, and dropping it would silently break class-presence assertions elsewhere.
      true: 'font-mono font-mono-face',
    },
  },
  defaultVariants: {
    variant: 'body-md',
    color: 'default',
  },
})

// ── Default HTML element per variant ───────────────────────────────

type TextVariant = NonNullable<VariantProps<typeof textVariants>['variant']>

const DEFAULT_ELEMENT: Record<TextVariant, React.ElementType> = {
  'display-lg':  'h1',
  'display-sm':  'h2',
  'headline-md': 'h2',
  'headline-sm': 'h3',
  'title-md':    'h4',
  'body-md':     'p',
  'body-sm':     'p',
  'label-lg':    'span',
  'label-sm':    'span',
}

// ── Text Component ─────────────────────────────────────────────────

export interface TextProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'color'>,
    VariantProps<typeof textVariants> {
  /** Render as a specific HTML element (overrides variant default) */
  as?: React.ElementType
  /** Line-clamp truncation — number of visible lines before ellipsis */
  truncate?: number
  /** Character-count truncation — slices string children at N chars */
  maxChars?: number
  children: React.ReactNode
  className?: string
  /** React 19: ref forwarded to the rendered element. */
  ref?: React.Ref<HTMLElement>
  /**
   * Only meaningful with `as="label"`, but the type has to admit it.
   *
   * `TextProps` extends `HTMLAttributes<HTMLElement>`, which has no `htmlFor` — that lives on
   * `LabelHTMLAttributes`. So a polymorphic component could render a `<label>` while its prop
   * type refused the one attribute a label needs, and every `<label className="text-label-sm">`
   * had to stay a raw element. A component's props must admit what its `as` makes reachable.
   */
  htmlFor?: string
}

export function Text({
  variant = 'body-md',
  color = 'default',
  weight,
  mono,
  as,
  truncate,
  maxChars,
  children,
  className,
  style,
  ref,
  ...rest
}: TextProps) {
  const Component = as ?? DEFAULT_ELEMENT[variant!]

  // Character truncation (only works on string children)
  let content = children
  if (maxChars != null && typeof children === 'string' && children.length > maxChars) {
    content = children.slice(0, maxChars) + '\u2026'
  }

  // Line-clamp styles
  const truncateStyles: React.CSSProperties | undefined = truncate
    ? {
        display: '-webkit-box',
        WebkitLineClamp: truncate,
        WebkitBoxOrient: 'vertical' as const,
        overflow: 'hidden',
        ...style,
      }
    : style

  return (
    <Component
      ref={ref}
      className={cn(textVariants({ variant, color, weight, mono }), className)}
      style={truncateStyles}
      {...rest}
    >
      {content}
    </Component>
  )
}

// ── Truncate Wrapper ───────────────────────────────────────────────

export interface TruncateProps {
  children: React.ReactNode
  /** Line-clamp truncation — number of visible lines */
  lines?: number
  /** Max width with text-overflow: ellipsis (single-line) */
  maxWidth?: string | number
  /** Character-count truncation (only works with string children) */
  chars?: number
  className?: string
}

export function Truncate({ children, lines, maxWidth, chars, className }: TruncateProps) {
  // Character truncation
  if (chars != null && typeof children === 'string' && children.length > chars) {
    return (
      <span className={className}>
        {children.slice(0, chars)}&hellip;
      </span>
    )
  }

  // Max-width single-line truncation
  if (maxWidth != null) {
    const width = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth
    return (
      <span
        className={cn('block overflow-hidden text-ellipsis whitespace-nowrap', className)}
        style={{ maxWidth: width }}
      >
        {children}
      </span>
    )
  }

  // Line-clamp multi-line truncation
  if (lines != null) {
    return (
      <div
        className={className}
        style={{
          display: '-webkit-box',
          WebkitLineClamp: lines,
          WebkitBoxOrient: 'vertical' as const,
          overflow: 'hidden',
        }}
      >
        {children}
      </div>
    )
  }

  // No truncation — passthrough
  return <>{children}</>
}
